import React from "react";
import { motion } from "framer-motion";
import { formatTimeForDisplay, getDayIndex } from "../../utils/scheduleUtils";

interface CalendarEvent {
  id: string;
  title: string;
  time: string;
  type: "appointment" | "class";
  day: number; // 0-6 (Sunday to Saturday)
  duration: number; // in hours
}

interface WeekCalendarProps {
  appointmentsEnabled: boolean;
  selectedAppointmentTypes?: string[];
  classesEnabled: boolean;
  brandColor?: string;
  selectedClassTypes?: string[];
  extractedScheduleData?: {
    success?: boolean;
    classes?: Array<{
      title?: string;
      dayOfWeek?: string;
      startTime?: string;
      endTime?: string;
    }>;
  };
  extractedAppointmentScheduleData?: {
    success?: boolean;
    classes?: Array<{
      title?: string;
      dayOfWeek?: string;
      startTime?: string;
      endTime?: string;
    }>;
  };
  currentStep?: string;
}

const WeekCalendar: React.FC<WeekCalendarProps> = ({
  appointmentsEnabled,
  selectedAppointmentTypes = [],
  classesEnabled,
  selectedClassTypes = [],
  extractedScheduleData,
  extractedAppointmentScheduleData,
}) => {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const hours = ["6 AM", "9 AM", "12 PM", "3 PM", "6 PM", "9 PM"];

  // Base slot definitions
  const APPOINTMENT_SLOTS = [
    { time: "10:00 AM", day: 1, duration: 1 }, // Monday - avoid 6am, 12pm, 6pm
    { time: "11:00 AM", day: 2, duration: 1 }, // Tuesday - avoid 9am, 3pm
    { time: "5:00 PM", day: 2, duration: 1 }, // Tuesday evening
    { time: "8:00 AM", day: 3, duration: 1 }, // Wednesday - avoid 6am, 12pm, 6pm
    { time: "10:00 AM", day: 4, duration: 1 }, // Thursday - avoid 9am, 3pm
    { time: "9:00 AM", day: 5, duration: 1 }, // Friday - avoid 6am, 12pm, 6pm
    { time: "3:00 PM", day: 5, duration: 1 }, // Friday afternoon
  ];

  const CLASS_SLOTS = [
    // MWF 6am classes
    { time: "6:00 AM", day: 1, duration: 1 },
    { time: "6:00 AM", day: 3, duration: 1 },
    { time: "6:00 AM", day: 5, duration: 1 },
    // MWF 12pm classes
    { time: "12:00 PM", day: 1, duration: 1 },
    { time: "12:00 PM", day: 3, duration: 1 },
    { time: "12:00 PM", day: 5, duration: 1 },
    // MWF 6pm classes
    { time: "6:00 PM", day: 1, duration: 1 },
    { time: "6:00 PM", day: 3, duration: 1 },
    { time: "6:00 PM", day: 5, duration: 1 },
    // TR 9am classes
    { time: "9:00 AM", day: 2, duration: 1 },
    { time: "9:00 AM", day: 4, duration: 1 },
    // TR 3pm classes
    { time: "3:00 PM", day: 2, duration: 1 },
    { time: "3:00 PM", day: 4, duration: 1 },
  ];

  const createEventsFromSlots = (
    slots: typeof APPOINTMENT_SLOTS,
    type: "appointment" | "class",
    typeList: string[],
    showTypes: boolean,
  ): CalendarEvent[] => {
    return slots.map((slot, index) => ({
      id: `${type}-${index}`,
      title:
        showTypes && typeList.length > 0
          ? typeList[index % typeList.length] ||
            (type === "appointment" ? "Appointment" : "Class")
          : "",
      time: slot.time,
      type,
      day: slot.day,
      duration: slot.duration,
    }));
  };

  const createEventsFromExtractedData = (
    extractedData: {
      classes?: Array<{
        title?: string;
        dayOfWeek?: string;
        startTime?: string;
        endTime?: string;
      }>;
    },
    type: "appointment" | "class",
  ): CalendarEvent[] => {
    if (!extractedData.classes) return [];

    return extractedData.classes
      .filter((item) => item.title && item.dayOfWeek && item.startTime)
      .map((item, index) => ({
        id: `${type}-extracted-${index}`,
        title: item.title!,
        time: formatTimeForDisplay(item.startTime),
        type,
        day: getDayIndex(item.dayOfWeek),
        duration: 1, // Assume 1 hour duration
      }));
  };

  const getEvents = (): CalendarEvent[] => {
    const events: CalendarEvent[] = [];

    if (appointmentsEnabled) {
      const hasExtractedAppointmentData =
        extractedAppointmentScheduleData?.success &&
        extractedAppointmentScheduleData?.classes?.length > 0;

      if (hasExtractedAppointmentData) {
        events.push(
          ...createEventsFromExtractedData(
            extractedAppointmentScheduleData,
            "appointment",
          ),
        );
      } else {
        const showAppointmentTypes = selectedAppointmentTypes.length > 0;
        events.push(
          ...createEventsFromSlots(
            APPOINTMENT_SLOTS,
            "appointment",
            selectedAppointmentTypes,
            showAppointmentTypes,
          ),
        );
      }
    }

    if (classesEnabled) {
      const hasExtractedData =
        extractedScheduleData?.success &&
        extractedScheduleData?.classes?.length > 0;

      if (hasExtractedData) {
        events.push(
          ...createEventsFromExtractedData(extractedScheduleData, "class"),
        );
      } else {
        const showClassNames = selectedClassTypes.length > 0;
        events.push(
          ...createEventsFromSlots(
            CLASS_SLOTS,
            "class",
            selectedClassTypes,
            showClassNames,
          ),
        );
      }
    }

    return events;
  };

  const events = getEvents();

  const getEventColor = (type: "appointment" | "class") => {
    if (type === "appointment") {
      return "#3B82F6"; // Blue for appointments
    } else {
      return "#10B981"; // Green for classes
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.1,
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={childVariants} className="tw-mb-4">
        <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">
          Weekly Schedule
        </h3>
        <div className="tw-flex tw-items-center tw-space-x-4 tw-text-sm">
          {appointmentsEnabled && (
            <div className="tw-flex tw-items-center tw-space-x-2">
              <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-blue-500"></div>
              <span className="tw-text-gray-600">Appointments</span>
            </div>
          )}
          {classesEnabled && (
            <div className="tw-flex tw-items-center tw-space-x-2">
              <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-green-500"></div>
              <span className="tw-text-gray-600">Classes</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Calendar Grid */}
      <motion.div variants={childVariants} className="tw-overflow-hidden">
        {/* Days Header */}
        <div className="tw-grid tw-gap-1 tw-mb-2 tw-grid-cols-[0.5fr_repeat(7,1fr)]">
          <div className="tw-h-8"></div> {/* Empty corner */}
          {days.map((day) => (
            <div
              key={day}
              className="tw-h-8 tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-gray-50 tw-rounded"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Time slots and events */}
        <div className="tw-grid tw-gap-1 tw-grid-cols-[0.5fr_repeat(7,1fr)]">
          {hours.map((hour, hourIndex) => (
            <React.Fragment key={hour}>
              {/* Time label */}
              <div className="tw-h-16 tw-flex tw-items-center tw-justify-end tw-pr-2 tw-text-xs tw-text-gray-500">
                {hour}
              </div>

              {/* Day columns */}
              {days.map((_, dayIndex) => (
                <div
                  key={`${hourIndex}-${dayIndex}`}
                  className="tw-h-16 tw-bg-gray-50 tw-border tw-border-gray-100 tw-rounded tw-relative tw-p-1"
                >
                  {/* Render events for this time slot */}
                  {events
                    .filter((event) => {
                      const eventHour = parseInt(event.time.split(":")[0]);
                      const eventPeriod = event.time.includes("PM")
                        ? "PM"
                        : "AM";
                      const event24Hour =
                        eventPeriod === "PM" && eventHour !== 12
                          ? eventHour + 12
                          : eventPeriod === "AM" && eventHour === 12
                            ? 0
                            : eventHour;

                      const slotHour24 =
                        hour.includes("PM") && !hour.includes("12 PM")
                          ? parseInt(hour.split(" ")[0]) + 12
                          : hour.includes("12 PM")
                            ? 12
                            : hour.includes("6 AM")
                              ? 6
                              : hour.includes("9 AM")
                                ? 9
                                : hour.includes("3 PM")
                                  ? 15
                                  : hour.includes("6 PM")
                                    ? 18
                                    : hour.includes("9 PM")
                                      ? 21
                                      : 0;

                      return (
                        event.day === dayIndex &&
                        Math.abs(event24Hour - slotHour24) <= 2
                      );
                    })
                    .map((event) => (
                      <motion.div
                        key={event.id}
                        className="tw-absolute tw-inset-1 tw-rounded tw-text-xs tw-text-white tw-p-1 tw-overflow-hidden"
                        style={{
                          backgroundColor: getEventColor(event.type),
                          height: `${Math.min(event.duration * 100, 90)}%`,
                        }}
                        variants={childVariants}
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        {event.title ? (
                          <>
                            <div className="tw-text-xs tw-leading-tight tw-font-medium">
                              {event.title}
                            </div>
                            <div className="tw-text-xs tw-opacity-90 tw-font-normal">
                              {event.time}
                            </div>
                          </>
                        ) : (
                          // Empty slot - just show the colored block without text
                          <div className="tw-w-full tw-h-full"></div>
                        )}
                      </motion.div>
                    ))}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WeekCalendar;
